/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */

import { convertHandleToTitleCase, convertToHandle } from '~/helpers/convert'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @param {Object} liquid - Liquid data.
 * @returns {Object}
 */
export function formatProduct(response, liquid) {
  if (!response) {
    return null
  }

  let product = response
  let siblings = []

  if (response.product) {
    product = response.product
  }

  if (!product || !product.handle) {
    return null
  }

  if (response.siblings) {
    siblings = transformEdges(response.siblings).map((sibling) => {
      const tag = product.tags?.find((value) => value.startsWith('colour:'))
      const label = (tag && convertToHandle(tag.replace('colour:', ''))) ?? ''

      const properties = {
        label: sibling.title,
        title: convertHandleToTitleCase(label),
        url: getProductUrl(sibling),
        selected: sibling.handle === product.handle,
        disabled: !sibling.available,
        asImage: true,
      }

      if (sibling.featuredImage) {
        properties.image = {
          src: sibling.featuredImage.url,
        }
      }

      return properties
    })
  }

  const formattedProduct = {
    available: product.available,
    bullets: product.bullets,
    compareAtPrice: formatPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.isGiftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    isGiftCard: product.isGiftCard,
    modelDetails: product.modelDetails,
    options: formatOptions(product),
    price: formatPrice(product.priceRange, liquid),
    siblings,
    subtitle: product.subtitle,
    tags: product.tags || [],
    title: product.title,
    type: product.productType,
    url: getProductUrl(product),
    variants: formatVariants(product, liquid),
    vendor: product.vendor,
    vipProduct: product.tags?.includes('vip') || false,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Formats price.
 * @param {Object} price - Price response.
 * @param {Object} liquid - Liquid data.
 * @returns {Object}
 */
function formatPrice(price, liquid) {
  const formattedPrice = {
    amount: Number(price?.minVariantPrice.amount) * 100 ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    max: Number(price?.maxVariantPrice.amount) * 100 ?? false,
    min: Number(price?.minVariantPrice.amount) * 100 ?? false,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }

  /**
   * Use Liquid prices for gift card.
   * - Converted pricing rounding isn't supported on gift cards but storefront
   *   API returns incorrect rounded prices regardless.
   */
  if (liquid && liquid?.isGiftCard) {
    formattedPrice.amount = liquid.price
    formattedPrice.max = liquid.price
    formattedPrice.min = liquid.price
  }

  return formattedPrice
}

/**
 * Formats images.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatImages(product) {
  if (!product.images) {
    return []
  }
  return transformEdges(product.images).map((image) => {
    const linkedVariant = product.variants
      ? transformEdges(product.variants).find((variant) => {
          return variant.image?.id === image.id
        })
      : null

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  if (!product.options) {
    return []
  }
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} response - GraphQL response.
 * @param {Object} liquid - Liquid data.
 * @returns {Array}
 */
function formatVariants(product, liquid) {
  if (!product.variants) {
    return []
  }
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: Number(variant.compareAtPrice?.amount) * 100 ?? false,
        currencyCode: variant.compareAtPrice?.currencyCode ?? false,
      },
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) ?? false,
      sku: variant.sku,
      inventory: variant.quantityAvailable,
      price: {
        amount: Number(variant.price.amount) * 100 ?? false,
        currencyCode: variant.price.currencyCode ?? false,
      },
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
      title: variant.title,
    }

    /**
     * Use Liquid prices for gift card.
     * - Converted pricing rounding isn't supported on gift cards but storefront
     *   API returns incorrect rounded prices regardless.
     */
    if (liquid && liquid.isGiftCard) {
      const match = liquid.variants?.find((liquidVariant) => {
        return formattedVariant.id === liquidVariant.id
      })

      if (match) {
        formattedVariant.price.amount = match.price
      }
    }

    return formattedVariant
  })
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/collections/${collection.handle}`
    : `//${location.host}${cnvs.routes.root}/collections/${collection.handle}`

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    title: collection.title,
    filters: collection.filters,
    url,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    return {
      handle: product.handle,
    }
  })

  return formattedCollection
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    return {
      handle,
      links,
      tier,
      title: object.title,
      url: object.url,
    }
  })
}

/**
 * Format image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  let filetype = src.match(/(?!\.)(?<filetype>jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)/gi)
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    window.console.log('Failed to find image filetype or version')
    return src
  }

  filetype = filetype[0]
  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove filetype.
   * - Remove sizing.
   * - Remove crop.
   * - Remove scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace(`.${filetype}`, '')
    .replace(/_crop_\w+/gi, '')
    .replace(/@\d+x/g, '')

  url = `https://${url}.${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/**
 * Returns the formatted path for the product.
 * @param {Object} product - The product object.
 * @returns {String}
 */
export function getProductUrl(product) {
  return cnvs.routes.root === '/'
    ? `//${location.host}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.root}/products/${product.handle}`
}
